import {
  createCalendarDate,
  getLondonTodayDate
} from '@/composables/useRosterRouteFilters';

const MAX_DATE_RANGE_DAYS = 40;
const MAX_EMPLOYEE_IDS = 2000;
const MAX_PAGE = 1000000;
const FILTER_TYPES = new Map([
  ['all', 'ALL'],
  ['late', 'LATE'],
  ['early', 'EARLY'],
  ['overtime', 'OVERTIME'],
  ['online', 'ONLINE'],
  ['notapproved', 'NOTAPPROVED']
]);
const SHIFT_TYPES = new Map([
  ['all', 'ALL'],
  ['shift', 'SHIFT'],
  ['cover', 'COVER']
]);
const NOT_LOGOUT_FILTERS = new Map([
  ['all', false],
  ['notlogout', true]
]);
const SORT_FIELDS = new Map([
  ['roster', 'ROSTER_START'],
  ['attendance', 'ATTENDANCE_START']
]);
const SORT_ORDERS = new Set(['asc', 'desc']);

const parseEmployeeIds = value => {
  if (value === 'all') return { employeeIds: [], error: null };
  if (typeof value !== 'string' || !value) {
    return { employeeIds: [], error: 'Employee IDs are required.' };
  }

  const segments = value.split(',');
  if (segments.some(segment => !/^\d+$/.test(segment))) {
    return { employeeIds: [], error: 'Employee IDs must be positive integers separated by commas.' };
  }

  const employeeIds = [...new Set(segments.map(Number))]
    .sort((firstId, secondId) => firstId - secondId);
  if (employeeIds.some(id => !Number.isSafeInteger(id) || id <= 0)) {
    return { employeeIds: [], error: 'One or more employee IDs are invalid.' };
  }
  if (employeeIds.length > MAX_EMPLOYEE_IDS) {
    return { employeeIds: [], error: `A maximum of ${MAX_EMPLOYEE_IDS} employee IDs is allowed.` };
  }
  return { employeeIds, error: null };
};

const parseCompanyId = value => {
  if (value === 'all') return { companyId: null, error: null };
  if (typeof value !== 'string' || !/^\d+$/.test(value)) {
    return { companyId: null, error: 'Company ID must be a positive integer or all.' };
  }
  const companyId = Number(value);
  if (!Number.isSafeInteger(companyId) || companyId <= 0) {
    return { companyId: null, error: 'Company ID is invalid.' };
  }
  return { companyId, error: null };
};

export const parseAttendanceRouteFilters = params => {
  const parsedCompany = parseCompanyId(params.companyId);
  if (parsedCompany.error) return { valid: false, error: parsedCompany.error };

  const parsedEmployees = parseEmployeeIds(params.employeeIds);
  if (parsedEmployees.error) return { valid: false, error: parsedEmployees.error };

  const filterType = typeof params.filterType === 'string'
    ? params.filterType.toLowerCase()
    : '';
  const typeCode = FILTER_TYPES.get(filterType);
  if (!typeCode) return { valid: false, error: 'Invalid attendance type filter.' };

  const shiftType = typeof params.shiftType === 'string'
    ? params.shiftType.toLowerCase()
    : '';
  const shiftTypeCode = SHIFT_TYPES.get(shiftType);
  if (!shiftTypeCode) return { valid: false, error: 'Invalid shift type filter.' };

  const notLogout = typeof params.notLogout === 'string'
    ? params.notLogout.toLowerCase()
    : '';
  const showNotLogout = NOT_LOGOUT_FILTERS.get(notLogout);
  if (showNotLogout === undefined) {
    return { valid: false, error: 'Invalid not logout filter.' };
  }

  if (typeof params.dateRange !== 'string') {
    return { valid: false, error: 'A date range is required.' };
  }
  const dateSegments = params.dateRange.split(',');
  if (dateSegments.length !== 2) {
    return { valid: false, error: 'Date range must contain a start and end date.' };
  }
  const startCalendarDate = createCalendarDate(dateSegments[0]);
  const endCalendarDate = createCalendarDate(dateSegments[1]);
  if (!startCalendarDate || !endCalendarDate) {
    return { valid: false, error: 'Dates must use YYYY-MM-DD format.' };
  }
  const durationDays = Math.round((endCalendarDate - startCalendarDate) / 86400000);
  if (durationDays < 0) {
    return { valid: false, error: 'Start date must be before or equal to end date.' };
  }
  if (durationDays > MAX_DATE_RANGE_DAYS) {
    return { valid: false, error: 'Maximum 40 days attendance can be generated.' };
  }

  const pageValue = typeof params.page === 'string' ? params.page : '';
  if (!/^\d+$/.test(pageValue)) {
    return { valid: false, error: 'Page must be a positive integer.' };
  }
  const page = Number(pageValue);
  if (!Number.isSafeInteger(page) || page <= 0 || page > MAX_PAGE) {
    return { valid: false, error: 'Page is outside the supported range.' };
  }

  return {
    valid: true,
    error: null,
    companyId: parsedCompany.companyId,
    employeeIds: parsedEmployees.employeeIds,
    filterType,
    typeCode,
    shiftType,
    shiftTypeCode,
    notLogout,
    showNotLogout,
    startDate: dateSegments[0],
    endDate: dateSegments[1],
    startCalendarDate,
    endCalendarDate,
    page
  };
};

export const parseAttendanceSortQuery = query => {
  const sort = typeof query.sort === 'string' ? query.sort.toLowerCase() : 'attendance';
  const order = typeof query.order === 'string' ? query.order.toLowerCase() : 'desc';
  const sortCode = SORT_FIELDS.get(sort);
  if (!sortCode || !SORT_ORDERS.has(order)) {
    return { valid: false, error: 'Invalid attendance sort options.' };
  }
  return {
    valid: true,
    error: null,
    sort,
    sortCode,
    order,
    sortOrder: order === 'asc' ? 1 : -1
  };
};

export const buildAttendanceRoute = ({
  companyId = null,
  employeeIds = [],
  filterType = 'all',
  shiftType = 'all',
  showNotLogout = false,
  startDate,
  endDate,
  page = 1,
  sort = 'attendance',
  order = 'desc'
}) => ({
  name: 'ViewAttendanceFiltered',
  params: {
    companyId: companyId ? String(companyId) : 'all',
    employeeIds: employeeIds.length
      ? [...new Set(employeeIds)].sort((firstId, secondId) => firstId - secondId).join(',')
      : 'all',
    filterType: filterType.toLowerCase(),
    shiftType: shiftType.toLowerCase(),
    notLogout: showNotLogout ? 'notlogout' : 'all',
    dateRange: `${startDate},${endDate}`,
    page: String(page)
  },
  query: { sort, order }
});

export const buildDefaultAttendanceRoute = () => {
  const today = getLondonTodayDate();
  return buildAttendanceRoute({ startDate: today, endDate: today });
};
